import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PostService } from '../../services/post.service';
import { UserSessionService } from '../../services/user-session.service';
import { API_BASE_URL } from '../../services/api-base';
import { toLocalDateTimeInputValue, toLocalISOString } from '../../utils/date-utils';

type EditorMode = 'default' | 'controlsign';
type PreviewType = 'image' | 'video' | 'document' | null;

@Component({
  selector: 'app-post-editor',
  templateUrl: './post-editor.component.html',
  styleUrl: './post-editor.component.scss'
})

export class PostEditorComponent implements OnInit {
  isSaving = false;
  feedback = '';
  file: File | null = null;
  filePreview: string | null = null;
  filePreviewType: PreviewType = null;
  carouselFiles: File[] = [];
  carouselPreviews: string[] = [];
  readonly maxCarouselImages = 5;
  readonly maxPostFileSizeMb = 200;
  readonly maxPostFileSizeBytes = this.maxPostFileSizeMb * 1024 * 1024;
  currentUser: any = null;
  private readonly apiBase = `${API_BASE_URL}`;
  editMode = false;
  editingPostId: number | null = null;
  existingMediaUrls: string[] = [];
  editorMode: EditorMode = 'default';
  editorTitle = 'Novo post da intranet';
  editorDescription = 'Compartilhe novidades com todos os colaboradores.';
  forceDocumentMode = false;
  documentPreviewName = '';
  private readonly documentAccept = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt';

  form = {
    post_type: 'image',
    content_text: '',
    allow_likes: true,
    allow_comments: true,
    show_popup: false,
    require_read_confirmation: false,
    require_acceptance: false,
    is_public_document: false,
    status: 'published',
    scheduled_at: '',
    popup_days: 1
  };
  dragging = false;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  constructor(
    private postService: PostService,
    private router: Router,
    private userSession: UserSessionService,
    private route: ActivatedRoute
  ) {
    this.currentUser = this.userSession.getUser();
  }

  ngOnInit() {
    this.editorMode = (this.route.snapshot.data?.['editorMode'] as EditorMode) || 'default';
    if (this.editorMode === 'controlsign') {
      this.enableControlSignMode();
    }

    this.route.queryParamMap.subscribe((params) => {
      const postId = params.get('postId');
      if (postId) {
        this.loadPostForEditing(Number(postId));
      } else {
        this.resetEditingState();
      }
    });
  }

  get isCarouselMode() {
    return this.form.post_type === 'carousel';
  }

  get isDocumentMode() {
    return this.form.post_type === 'document';
  }

  get uploadAccept() {
    if (this.isCarouselMode) {
      return 'image/*';
    }
    if (this.isDocumentMode || this.forceDocumentMode) {
      return this.documentAccept;
    }
    return 'image/*,video/*';
  }

  private enableControlSignMode() {
    this.forceDocumentMode = true;
    this.form.post_type = 'document';
    this.form.allow_likes = false;
    this.form.allow_comments = false;
    this.form.require_read_confirmation = true;
    this.form.require_acceptance = false;
    this.form.status = 'published';
    this.editorTitle = 'Novo ControlSign';
    this.editorDescription = 'Compartilhe documentos importantes da organização e solicite confirmações de leitura.';
  }

  onPopupToggle() {
    if (this.form.show_popup) {
      this.form.allow_likes = false;
      this.form.allow_comments = false;
      this.form.post_type = 'image';
      this.form.popup_days = this.form.popup_days || 1;
      this.form.status = 'published';
    } else {
      this.form.popup_days = 1;
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.dragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.dragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.dragging = false;
    const files = Array.from(event.dataTransfer?.files || []);
    if (this.isCarouselMode) {
      this.handleCarouselFiles(files);
    } else {
      this.handleSingleFile(files[0] || null);
    }
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (this.isCarouselMode) {
      this.handleCarouselFiles(files);
    } else {
      this.handleSingleFile(files[0] || null);
    }
    if (input.value) {
      input.value = '';
    }
  }

  triggerFileInput() {
    this.fileInput?.nativeElement.click();
  }

  private handleSingleFile(file: File | null) {
    if (!file) {
      this.resetSingleMedia();
      return;
    }
    if (!this.isFileSizeAllowed(file)) {
      this.resetSingleMedia();
      return;
    }
    if (this.form.show_popup && !file.type.startsWith('image/')) {
      this.feedback = 'Popups aceitam apenas imagens.';
      return;
    }
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    const allowDocument = this.isDocumentMode || this.forceDocumentMode;
    if (!isImage && !isVideo && !allowDocument) {
      this.feedback = 'Envie apenas imagens ou vídeos.';
      this.resetSingleMedia();
      return;
    }
    this.file = file;
    const treatAsDocument = allowDocument || (!isImage && !isVideo);
    if (treatAsDocument) {
      this.filePreviewType = 'document';
      this.documentPreviewName = file.name || 'Documento selecionado';
      this.filePreview = null;
      this.form.post_type = 'document';
      this.form.allow_likes = false;
      this.form.allow_comments = false;
      return;
    }

    this.filePreviewType = isVideo ? 'video' : 'image';
    this.documentPreviewName = '';
    if (this.filePreviewType === 'video') {
      this.form.post_type = 'video';
    } else if (this.form.post_type === 'video') {
      this.form.post_type = 'image';
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.filePreview = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  private handleCarouselFiles(files: File[]) {
    if (!files.length) {
      return;
    }
    const oversized = files.find(file => !this.isFileSizeAllowed(file));
    if (oversized) {
      return;
    }
    const available = this.maxCarouselImages - this.carouselFiles.length;
    if (available <= 0) {
      this.feedback = `Limite de ${this.maxCarouselImages} imagens atingido.`;
      return;
    }

    const validFiles = files.filter(file => file.type.startsWith('image/'));
    if (!validFiles.length) {
      this.feedback = 'Envie apenas arquivos de imagem para o carrossel.';
      return;
    }

    validFiles.slice(0, available).forEach((file) => {
      this.carouselFiles.push(file);
      const reader = new FileReader();
      reader.onload = () => {
        this.carouselPreviews.push(reader.result as string);
      };
      reader.readAsDataURL(file);
    });

    if (validFiles.length > available || files.length > validFiles.length) {
      this.feedback = `Até ${this.maxCarouselImages} imagens são permitidas.`;
    }
  }

  private isFileSizeAllowed(file: File) {
    if (file.size <= this.maxPostFileSizeBytes) {
      return true;
    }
    this.feedback = `Arquivo excede o limite de ${this.maxPostFileSizeMb}MB.`;
    return false;
  }

  removeCarouselImage(index: number) {
    this.carouselFiles.splice(index, 1);
    this.carouselPreviews.splice(index, 1);
  }

  submitPost() {
    if (!this.currentUser?.id || this.isSaving) {
      return;
    }
    if ((this.isDocumentMode || this.forceDocumentMode) && !this.editMode && !this.file) {
      this.feedback = 'Anexe o documento que será distribuído.';
      return;
    }
    if (this.form.show_popup && !this.editMode && !this.file) {
      this.feedback = 'Selecione uma imagem para o popup.';
      return;
    }
    const hasNewMedia = this.file || this.carouselFiles.length;
    if (!this.editMode && this.isCarouselMode) {
      if (!this.carouselFiles.length) {
        this.feedback = 'Adicione pelo menos uma imagem ao carrossel.';
        return;
      }
    } else if (!this.editMode && !this.form.content_text && !this.file) {
      this.feedback = 'Adicione um texto ou um arquivo.';
      return;
    }

    if (this.editMode && this.editingPostId && !hasNewMedia) {
      this.saveEdition();
      return;
    }

    const payload = new FormData();
    payload.append('tenant_id', this.currentUser.tenant_id);
    payload.append('user_id', this.currentUser.id);
    payload.append('post_type', this.form.post_type);
    payload.append('content_text', this.form.content_text || '');
    payload.append('allow_likes', this.form.allow_likes ? '1' : '0');
    payload.append('allow_comments', this.form.allow_comments ? '1' : '0');
    payload.append('show_popup', this.form.show_popup ? '1' : '0');
    payload.append('require_read_confirmation', this.form.require_read_confirmation ? '1' : '0');
    payload.append('require_acceptance', this.form.require_acceptance ? '1' : '0');
    payload.append('is_public_document', this.form.is_public_document ? '1' : '0');
    payload.append('status', this.form.status);
    if (this.form.scheduled_at) {
      payload.append('scheduled_at', this.form.scheduled_at);
      payload.append('posted_at', this.form.scheduled_at);
    } else {
      payload.append('posted_at', toLocalISOString(new Date()));
    }
    const popupExpiresAt = this.form.show_popup ? this.buildPopupExpiration() : null;
    if (popupExpiresAt) {
      payload.append('popup_expires_at', popupExpiresAt);
    }

    if (this.isCarouselMode) {
      this.carouselFiles.forEach((file) => payload.append('carousel_files', file));
    } else if (this.file) {
      payload.append('file', this.file);
    }

    this.isSaving = true;
    this.feedback = '';

    this.postService.createPost(payload).subscribe({
      next: () => {
        this.isSaving = false;
        this.feedback = 'Post enviado com sucesso!';
        setTimeout(() => this.router.navigate(['/intranet-dash']), 1000);
      },
      error: (err) => {
        this.feedback = err?.error?.error || 'Erro ao enviar o post.';
      }
    });
  }

  get isErrorFeedback() {
    if (!this.feedback) {
      return false;
    }
    const normalized = this.feedback.toLowerCase();
    return normalized.includes('erro') || normalized.includes('não');
  }
  
  private saveEdition() {
    if (!this.editMode || !this.editingPostId) {
      return;
    }
    const payload: any = {
      content_text: this.form.content_text || '',
      allow_likes: this.form.allow_likes,
      allow_comments: this.form.allow_comments,
      show_popup: this.form.show_popup,
      require_read_confirmation: this.form.require_read_confirmation,
      require_acceptance: this.form.require_acceptance,
      is_public_document: this.form.is_public_document,
      status: this.form.status,
      post_type: this.form.post_type,
      scheduled_at: this.form.scheduled_at || null,
      posted_at: this.form.status === 'published' && !this.form.scheduled_at ? toLocalISOString(new Date()) : this.form.scheduled_at || null,
      updated_by: this.currentUser?.id
    };
    payload.popup_expires_at = this.form.show_popup ? this.buildPopupExpiration() : null;
    this.isSaving = true;
    this.feedback = '';
    this.postService.updatePost(this.editingPostId, payload).subscribe({
      next: () => {
        this.isSaving = false;
        this.feedback = 'Post atualizado com sucesso!';
        setTimeout(() => this.router.navigate(['/intranet-dash']), 1000);
      },
      error: (err) => {
        this.feedback = err?.error?.error || 'Não foi possível atualizar o post.';
      }
    });
  }
  
  onScheduleChange() {
    if (this.form.scheduled_at) {
      this.form.status = 'scheduled';
    } else {
      this.form.status = 'published';
    }
  }

  onStatusChange(value: string) {
    this.form.status = value as 'published' | 'scheduled' | 'draft';
    if (value === 'published') {
      this.form.scheduled_at = '';
    }
  }

  onPostTypeChange(value: string) {
    if (this.forceDocumentMode) {
      this.form.post_type = 'document';
      return;
    }
    this.form.post_type = value;
    if (this.isCarouselMode) {
      this.resetSingleMedia();
    } else {
      this.carouselFiles = [];
      this.carouselPreviews = [];
    }
    if (value === 'document') {
      this.form.allow_likes = false;
      this.form.allow_comments = false;
      this.form.require_read_confirmation = true;
      this.resetSingleMedia();
    } else if (!this.form.show_popup) {
      this.form.allow_likes = true;
      this.form.allow_comments = true;
      if (this.form.post_type !== 'video') {
        this.filePreviewType = 'image';
      }
    }
  }

  private resetSingleMedia() {
    this.file = null;
    this.filePreview = null;
    this.filePreviewType = null;
    this.documentPreviewName = '';
  }

  private loadPostForEditing(postId: number) {
    this.editMode = true;
    this.editingPostId = postId;
    this.postService.getPostById(postId).subscribe({
      next: (post) => {
        this.form.post_type = post.post_type || 'image';
        this.form.content_text = post.content_text || '';
        this.form.allow_likes = this.parseFlag(post.allow_likes, true);
        this.form.allow_comments = this.parseFlag(post.allow_comments, true);
        this.form.show_popup = this.parseFlag(post.show_popup);
        this.form.require_read_confirmation = this.parseFlag(post.require_read_confirmation);
        this.form.require_acceptance = this.parseFlag(post.require_acceptance);
        this.form.is_public_document = this.parseFlag(post.is_public_document);
        this.form.status = post.status || 'draft';
        this.form.scheduled_at = post.scheduled_at ? toLocalDateTimeInputValue(new Date(post.scheduled_at)) : '';
        this.form.popup_days = this.form.show_popup ? this.derivePopupDays(post.popup_expires_at) : 1;
        this.existingMediaUrls = Array.isArray(post.media_urls) ? post.media_urls : [];
        if (this.form.post_type === 'document') {
          this.forceDocumentMode = this.forceDocumentMode || post.post_type === 'document';
          this.documentPreviewName = (post.file_url && post.file_url.split('/').pop()) || 'Documento existente';
          this.filePreviewType = 'document';
          this.form.allow_likes = false;
          this.form.allow_comments = false;
          this.form.require_read_confirmation = true;
        }
        if (this.form.show_popup) {
          this.onPopupToggle();
        }
      },
      error: () => {
        this.feedback = 'Não foi possível carregar o post para edição.';
      }
    });
  }

  private resetEditingState() {
    this.editMode = false;
    this.editingPostId = null;
    this.existingMediaUrls = [];
    this.form.is_public_document = false;
  }

  private parseFlag(value: any, fallback = false) {
    if (value === undefined || value === null) {
      return fallback;
    }
    if (typeof value === 'boolean') {
      return value;
    }
    const normalized = Number(value);
    if (!Number.isNaN(normalized)) {
      return normalized === 1;
    }
    return Boolean(value);
  }

  private buildPopupExpiration(): string | null {
    const days = Number(this.form.popup_days);
    if (!days || days <= 0) {
      return null;
    }
    const limit = new Date();
    limit.setHours(23, 59, 59, 999);
    limit.setDate(limit.getDate() + days);
    return toLocalISOString(limit);
  }

  private derivePopupDays(expiresAt?: string | null): number {
    if (!expiresAt) {
      return 1;
    }
    const end = new Date(expiresAt);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    if (diff <= 0) {
      return 1;
    }
    return Math.max(1, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }

  formatExistingMedia(path: string) {
    if (!path) {
      return '';
    }
    if (path.startsWith('http')) {
      return path;
    }
    return `${this.apiBase}${path}`;
  }
}
